/* ================= BECOME AN ASTROLOGER (dashboard) =================
   The onboarding application for an account that ALREADY exists —
   opened from the "Become an Astrologer" button the admin switches
   on per role / per account (Settings → Feature Visibility in the
   admin panel; useAstrologerOnboarding reads the decision).

   Same shape as the astrologer My Profile screen: the onboarding
   wizard's OWN step panels (@astro/astrologer-forms) stacked as
   sections on one page, so there is one definition of the fields,
   widgets and validation across the public wizard, this screen and
   profile editing. Unlike My Profile this keeps the Agreement step
   (declarations + signature — it IS an application) and drops the
   password field: the applicant is signed in already, and email and
   mobile are shown read-only because the submit never changes them.

   Submit: POST /astrologer/onboarding (multipart, so the photo and
   KYC documents travel with it). The profile is created inactive
   and pending — an admin approves it in KYC Review before the
   astrologer is bookable, which is what the states below report. */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import { FormProvider, useForm } from '@astro/forms'
import {
  STEPS, DEFAULT_VALUES, buildProfilePrefill, buildOnboardingFormData, toRegistrationFieldErrors,
  StepBasicInfo, StepProfile, StepEducation, StepProfessional,
  StepAvailability, StepKyc, StepBank, StepAgreement,
} from '@astro/astrologer-forms'
import { useAstrologerOnboarding, astrologerOnboardingKey } from '../../useAstrologerOnboarding'

/* Every wizard step, in order — `index` points into STEPS for the matching
   icon/heading/subheading. The Agreement step is included (this is the
   application, not a profile edit); only the password input is dropped. */
const SECTIONS = [
  { index: 0, Panel: StepBasicInfo, props: { showPassword: false, lockAccountFields: true } },
  { index: 1, Panel: StepProfile },
  { index: 2, Panel: StepEducation },
  { index: 3, Panel: StepProfessional },
  { index: 4, Panel: StepAvailability },
  { index: 5, Panel: StepKyc },
  { index: 6, Panel: StepBank },
  { index: 7, Panel: StepAgreement },
]

/** Unwrap a `{ status, data }` envelope (or pass a bare value through). */
function unwrap(res) {
  if (res && typeof res === 'object' && 'data' in res) return res.data
  return res
}

/* ── The states an application can be in, in the applicant's words ── */
const REVIEW_STATES = {
  pending: {
    badge: 'Under review',
    tone: 'amber',
    title: 'Your application is with our team',
    body:
      'We are verifying your profile, credentials and KYC documents. You will be notified once the review is complete — nothing more is needed from you right now.',
  },
  approved: {
    badge: 'Approved',
    tone: 'green',
    title: 'You are a verified astrologer 🎉',
    body:
      'Your astrologer tools (consultations, availability, earnings and your astrologer profile) appear in this dashboard the next time you sign in.',
  },
  rejected: {
    badge: 'Needs attention',
    tone: 'red',
    title: 'Your application could not be approved',
    body:
      'Our team was unable to verify the details submitted. Please reach out through Help & Support and we will walk you through what to correct.',
  },
}

const TONES = {
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  red: 'bg-red-50 text-red-600 border-red-200',
}

function ApplicationStatusCard({ application, onSwitch }) {
  const state = REVIEW_STATES[application.status] ?? REVIEW_STATES.pending
  const submitted = application.submitted_at
    ? new Date(application.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  return (
    <div className="space-y-6">
      <div className="dash-card p-6 sm:p-8 text-center">
        <span className="text-4xl">🪷</span>
        <span className={`block mx-auto w-fit mt-4 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${TONES[state.tone]}`}>
          {state.badge}
        </span>
        <h3 className="text-xl sm:text-2xl font-royal text-astroDark mt-4">{state.title}</h3>
        <p className="text-xs sm:text-sm text-stone-500 mt-2.5 max-w-xl mx-auto leading-relaxed">{state.body}</p>
        {submitted && <p className="text-[11px] text-stone-400 mt-4">Submitted on {submitted}</p>}
        {application.status === 'approved' && !application.is_live && (
          <p className="text-[11px] text-stone-400 mt-2">
            Your public listing is switched off at the moment, so seekers cannot book you yet.
          </p>
        )}
        {application.status === 'rejected' && (
          <button
            type="button"
            onClick={() => onSwitch?.('support')}
            className="shine-btn mt-6 bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5"
          >
            Contact Support
          </button>
        )}
      </div>
    </div>
  )
}

/* ── Skeleton while the account prefill loads — mirrors the page below ── */
function SectionSkeleton({ fields = 4 }) {
  return (
    <div className="dash-card p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-stone-100 shrink-0" />
        <div className="space-y-2">
          <div className="h-3.5 w-40 bg-stone-100 rounded-full" />
          <div className="h-2 w-56 bg-stone-100 rounded-full" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(fields)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-2 w-24 bg-stone-100 rounded-full" />
            <div className="h-11 bg-stone-100 rounded-xl" />
          </div>
        ))}
      </div>
    </div>
  )
}

export default function BecomeAstrologerTab({ onSwitch }) {
  const { user, reloadSession } = useAuth()
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null) // { tone: 'ok' | 'err', text }

  const { canApply, application, isPending: statusPending } = useAstrologerOnboarding()

  /* Account prefill — the applicant's own name, email and mobile. Best-effort:
     a failed read just leaves those inputs blank (email/mobile are read-only,
     so the account's own values are what the API uses either way). */
  const meQuery = useQuery({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api.dashboard.me(),
    enabled: Boolean(user) && !application,
    retry: false,
    select: unwrap,
  })

  // buildProfilePrefill maps the account fields (name split, email, mobile)
  // onto the wizard's field names; everything else starts at its blank default.
  const prefill = useMemo(() => {
    const base = buildProfilePrefill(meQuery.data ?? {}, {})
    return {
      ...base,
      // The declaration is signed in the applicant's own legal name, which the
      // account already knows — one less thing to retype.
      applicant_name: base.display_name || [base.first_name, base.last_name].filter(Boolean).join(' '),
      declaration_date: DEFAULT_VALUES.declaration_date,
    }
  }, [meQuery.data])

  const methods = useForm({
    defaultValues: prefill,
    values: prefill,
    resetOptions: { keepDirtyValues: true },
    mode: 'onTouched',
  })
  const { handleSubmit, setError } = methods

  const applyMutation = useMutation({
    mutationFn: (payload) => api.astrologers.applyOnboarding(payload),
    onSuccess: async () => {
      setBanner(null)
      // Re-read the decision so this screen flips to the review state, and the
      // session so the newly granted astrologer role lands as soon as upstream
      // reports it.
      await queryClient.invalidateQueries({ queryKey: astrologerOnboardingKey })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'astrologer-profile'] })
      reloadSession?.()
    },
    onError: (err) => {
      // A 422 carries per-field messages; the wizard's field names match the
      // payload keys, so they surface on their own inputs (the step they belong
      // to is already on screen — every section is rendered at once here).
      const fieldErrors = toRegistrationFieldErrors(err?.error?.details)
      fieldErrors.forEach(({ name, message }) => setError(name, { type: 'server', message }))
      setBanner({
        tone: 'err',
        text:
          err?.error?.status === 422 && err.message
            ? err.message
            : 'Your application could not be submitted. Please check your details and try again.',
      })
    },
  })

  const onSubmit = handleSubmit(
    // Multipart, so the profile photo and KYC documents upload with it.
    (values) => {
      setBanner(null)
      applyMutation.mutate(buildOnboardingFormData(values))
    },
    () => setBanner({ tone: 'err', text: 'Please complete the highlighted fields before submitting.' }),
  )

  /* ── Already applied: report where the review is, never the form again ── */
  if (application) return <ApplicationStatusCard application={application} onSwitch={onSwitch} />

  /* ── The admin turned the prompt off between loading the page and here ── */
  if (!statusPending && !canApply) {
    return (
      <div className="dash-card p-6 sm:p-8 text-center">
        <span className="text-3xl">🔒</span>
        <h3 className="text-lg font-royal text-astroDark mt-3">Astrologer onboarding isn&apos;t open for your account</h3>
        <p className="text-xs text-stone-500 mt-2 max-w-md mx-auto leading-relaxed">
          Applications are currently invitation-only. Reach out through Help &amp; Support if you would like to join
          our panel of astrologers.
        </p>
      </div>
    )
  }

  const settled = (q) => q.isSuccess || q.isError
  if (statusPending || !settled(meQuery)) {
    return (
      <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="Loading the astrologer application">
        <SectionSkeleton fields={4} />
        <SectionSkeleton fields={6} />
        <SectionSkeleton fields={2} />
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      {/* .astro-form scopes the wizard's ar-* field styles (kit styles.css) */}
      <form onSubmit={onSubmit} className="astro-form space-y-6" noValidate>
        {/* ── What this is, before the first field ── */}
        <div className="dash-card p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-[#F5E6D3] flex items-center justify-center text-3xl shrink-0">🪷</div>
          <div className="flex-1 min-w-0">
            <p className="text-[8px] uppercase font-bold tracking-[0.3em] text-[#A67C00]">Join Our Panel</p>
            <h3 className="text-xl text-astroDark font-royal mt-0.5">Become a Verified Astrologer</h3>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed">
              Your account stays exactly as it is — this adds your astrologer profile, credentials and KYC on top of
              it. Everything is reviewed by our team before your listing goes live, and you keep access to this
              dashboard throughout.
            </p>
          </div>
        </div>

        {/* ── The wizard's step panels, one card per step ── */}
        {SECTIONS.map(({ index, Panel, props }) => {
          const meta = STEPS[index]
          return (
            <div key={meta.title} className="dash-card p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-9 h-9 rounded-xl bg-[#F5E6D3] flex items-center justify-center text-base shrink-0">{meta.icon}</span>
                <div>
                  <h3 className="text-lg text-astroDark font-royal leading-tight">{meta.heading}</h3>
                  <p className="text-[11px] text-stone-500">{meta.subheading}</p>
                </div>
              </div>
              <Panel {...(props ?? {})} />
            </div>
          )
        })}

        {/* ── Submit bar ── */}
        <div className="dash-card p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            {banner ? (
              <p className={`text-xs font-semibold ${banner.tone === 'ok' ? 'text-green-600' : 'text-red-500'}`}>{banner.text}</p>
            ) : (
              <p className="text-xs text-stone-500">
                Review everything above, then submit — our team usually completes the review within 24 hours.
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={applyMutation.isPending}
            className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 shrink-0"
          >
            {applyMutation.isPending ? 'Submitting…' : 'Submit Application'}
          </button>
        </div>
      </form>
    </FormProvider>
  )
}
